import { type ReactNode } from 'react';
import toast from 'react-hot-toast/headless';
import { useMedia } from 'react-use';
import { Toast, ToastVariant } from '@hungryroot/components-toast';
import { useExperimentFlags } from '@hungryroot/experiments';
import { FavoriteGroceries } from '@hungryroot/favorites';
import { useIdentify, useNextOrderV3 } from '@hungryroot/rest-hooks';
import { type Location, productInteraction } from '@hungryroot/segment';
import { IconButton, type IconButtonProps } from '@hungryroot/ui-kit';
import { CaretLeft, BreakpointQueryLarge } from '@hungryroot/ui-kit';
import { ShareButtonContainer } from '../ShareButtonContainer/ShareButtonContainer';
import { type PDPInteractionOverrides, type StackProps } from './PDPModal';

import styles from './PDPModalIcons.module.css';

const getIconButtonProps = (
  scrolled: boolean
): Pick<IconButtonProps, 'prominence' | 'style' | 'size'> => {
  return {
    prominence: scrolled ? 'tertiary' : 'primary',
    size: 'small',
    style: scrolled ? 'muted' : 'inverted',
  };
};

type ScrollProps = {
  scrolled: boolean;
};

type BackIconProps = {
  onClick: () => void;
} & ScrollProps;

const BackIcon = ({ scrolled, onClick }: BackIconProps) => (
  <IconButton
    ariaLabel="back"
    icon={<CaretLeft />}
    onClick={onClick}
    {...getIconButtonProps(scrolled)}
  />
);

type SearchAttribution = {
  searchId: string;
  resultPosition: number;
} | null;

const resolveLocation = (
  defaultLocation: Location,
  interactionOverrides?: PDPInteractionOverrides
): Location | null =>
  interactionOverrides && 'location' in interactionOverrides
    ? interactionOverrides.location ?? null
    : defaultLocation;

type FavoriteIconProps = {
  onFavorite?: () => void;
  exclude?: boolean;
  product: { id: number; name: string };
  interactionOverrides?: PDPInteractionOverrides;
  searchAttribution?: SearchAttribution;
} & ScrollProps;

const FavoriteIcon = ({
  exclude,
  product,
  scrolled,
  onFavorite,
  interactionOverrides,
  searchAttribution,
}: FavoriteIconProps) => {
  const { customerData } = useIdentify();
  const isDesktop = useMedia(BreakpointQueryLarge);
  const { popsicleExp242 } = useExperimentFlags([242]);

  // Exp 242 (popsicle): the favorite heart moves to the bottom action bar (see
  // usePdpFooter), so hide it from the header on mobile. Mirrors
  // useIsMobilePopsicleEnabled (app-mhr), replicated here because the shared rdp
  // package cannot depend on app-mhr.
  const isMobilePopsicle = !isDesktop && Boolean(popsicleExp242?.isVariant);

  if (exclude || isMobilePopsicle) return null;

  const resolvedLocation = resolveLocation('pdp', interactionOverrides);

  return customerData?.id ? (
    <FavoriteGroceries
      customerId={customerData?.id}
      productId={product.id}
      productName={product.name}
      segment={{
        location: resolvedLocation,
        pageName: interactionOverrides?.pageName,
        accessedFrom: interactionOverrides?.accessedFrom,
        conversationId: interactionOverrides?.conversationId,
        ...(searchAttribution && {
          searchId: searchAttribution.searchId,
          resultPosition: searchAttribution.resultPosition,
          accessedFrom:
            interactionOverrides?.accessedFrom ?? 'search_results_list',
        }),
      }}
      onFavorite={onFavorite}
      iconButtonVariant={getIconButtonProps(scrolled)}
      variant="PDP"
    />
  ) : null;
};

type ShareButtonProps = {
  onShareSuccess?: () => void;
  exclude?: boolean;
  product: {
    id: number;
    slug?: string;
    name: string;
  };
  interactionOverrides?: PDPInteractionOverrides;
  searchAttribution?: SearchAttribution;
} & ScrollProps;

const ShareButton = ({
  exclude,
  product,
  scrolled,
  onShareSuccess,
  interactionOverrides,
  searchAttribution,
}: ShareButtonProps) => {
  const { id, name, slug } = product;
  const isDesktop = useMedia(BreakpointQueryLarge);
  const { order } = useNextOrderV3();

  if (exclude) return null;

  const resolvedLocation = resolveLocation('pdp', interactionOverrides);

  return (
    <ShareButtonContainer
      title={name}
      text={name}
      {...getIconButtonProps(scrolled)}
      url={`${window.location.origin}/products/${slug}/`}
      onShareSuccess={() => {
        productInteraction({
          productId: id,
          productName: name,
          actionType: 'shared',
          orderId: order?.id,
          location: resolvedLocation,
          pageName: interactionOverrides?.pageName,
          accessedFrom: interactionOverrides?.accessedFrom,
          conversationId: interactionOverrides?.conversationId,
          ...(searchAttribution && {
            searchId: searchAttribution.searchId,
            resultPosition: searchAttribution.resultPosition,
            accessedFrom:
              interactionOverrides?.accessedFrom ?? 'search_results_list',
          }),
        });
        onShareSuccess?.();
        if (isDesktop) {
          toast(<Toast variant={ToastVariant.Success}>Link copied</Toast>);
        }
      }}
    />
  );
};

export type GetIconsArgs = StackProps & {
  product?: { id: number; name: string; slug?: string };
  exclusions?: ('favorite' | 'share')[];
  onShareSuccess?: () => void;
  onFavorite?: () => void;
  interactionOverrides?: PDPInteractionOverrides;
  searchAttribution?: SearchAttribution;
} & ScrollProps;

export const Icons = (args: GetIconsArgs): ReactNode => {
  const { exclusions, product, scrolled, interactionOverrides } = args;

  if (!product) return null;

  return (
    <div className={styles['header-icons']}>
      {args.stacked && (
        <BackIcon onClick={args.onBackClick} scrolled={scrolled} />
      )}
      <ShareButton
        exclude={exclusions?.includes('share')}
        product={product}
        scrolled={scrolled}
        onShareSuccess={args.onShareSuccess}
        interactionOverrides={interactionOverrides}
        searchAttribution={args.searchAttribution}
      />
      <FavoriteIcon
        exclude={exclusions?.includes('favorite')}
        product={product}
        scrolled={scrolled}
        onFavorite={args.onFavorite}
        interactionOverrides={interactionOverrides}
        searchAttribution={args.searchAttribution}
      />
    </div>
  );
};
